<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增取貨預約
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋訂單編號、姓名、電話..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStoreId" class="form-control" @change="handleFilterChange">
          <option value="">全部門市</option>
          <option v-for="store in stores" :key="store.id" :value="store.id">
            {{ store.storeName }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="pending">待取貨</option>
          <option value="ready">已備貨</option>
          <option value="picked_up">已取貨</option>
          <option value="expired">已過期</option>
        </select>
      </div>
      <div class="filter-group">
        <input
          v-model="filterDate"
          type="date"
          class="form-control"
          @change="handleFilterChange"
        />
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchAppointments">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>取貨日期</th>
            <th>取貨時段</th>
            <th>訂單編號</th>
            <th>會員</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="appointments.length === 0">
            <td colspan="7" class="empty-state">
              沒有找到取貨預約記錄
            </td>
          </tr>
          <tr v-for="appointment in appointments" :key="appointment.id">
            <td>{{ formatDate(appointment.pickupDate) }}</td>
            <td>{{ appointment.pickupTimeSlot || '-' }}</td>
            <td>{{ appointment.orderId || '-' }}</td>
            <td>{{ appointment.memberId || '-' }}</td>
            <td>
              <span :class="getStatusBadgeClass(appointment.status)">
                {{ getStatusText(appointment.status) }}
              </span>
            </td>
            <td>{{ formatDateTime(appointment.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleView(appointment)"
                  title="查看詳情"
                >
                  <i class="ri-eye-line"></i>
                </button>
                <button
                  v-if="appointment.status === 'ready'"
                  class="btn btn-sm btn-success"
                  @click="handleConfirmPickup(appointment)"
                  title="確認取貨"
                >
                  <i class="ri-check-line"></i>
                </button>
                <button
                  v-if="appointment.status === 'pending'"
                  class="btn btn-sm btn-danger"
                  @click="handleCancel(appointment)"
                  title="取消預約"
                >
                  <i class="ri-close-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pickupAppointmentService } from '@/api/modules/pickupAppointments.js';
import { storeService } from '@/api/modules/stores.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'ClientPickupAppointments',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('取貨預約');
    const appointments = ref([]);
    const stores = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStoreId = ref('');
    const filterStatus = ref('');
    const filterDate = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);

    const totalPages = computed(() => Math.ceil(total.value / pageSize.value));

    const fetchStores = async () => {
      try {
        const response = await storeService.getStores({ page: 1, pageSize: 100 });
        if (response.success && response.data?.items) {
          stores.value = response.data.items;
        }
      } catch (err) {
        console.error('Failed to fetch stores:', err);
      }
    };

    const fetchAppointments = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
          search: searchQuery.value || undefined,
          storeId: filterStoreId.value || undefined,
          status: filterStatus.value || undefined,
          startDate: filterDate.value || undefined,
          endDate: filterDate.value || undefined
        };

        const response = await pickupAppointmentService.getPickupAppointments(params);
        
        if (response.success && response.data) {
          appointments.value = response.data.items || [];
          total.value = response.data.pagination?.totalItems || 0;
        } else {
          throw new Error('查詢失敗');
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '查詢取貨預約記錄失敗';
        console.error('Failed to fetch appointments:', err);
      } finally {
        loading.value = false;
      }
    };

    const handleCreate = () => {
      router.push('/client/pickup-appointments/create');
    };

    const handleView = (appointment) => {
      router.push(`/client/pickup-appointments/${appointment.id}`);
    };

    const handleConfirmPickup = async (appointment) => {
      try {
        await pickupAppointmentService.confirmPickup(appointment.id, {
          pickedUpBy: '管理員'
        });
        showSuccess('已確認取貨');
        fetchAppointments();
      } catch (err) {
        showError(err.response?.data?.message || '確認取貨失敗');
      }
    };

    const handleCancel = async (appointment) => {
      const confirmed = await showDeleteConfirm('確定要取消此取貨預約嗎？');
      if (!confirmed) return;

      try {
        await pickupAppointmentService.cancelPickupAppointment(appointment.id);
        showSuccess('取貨預約已取消');
        fetchAppointments();
      } catch (err) {
        showError(err.response?.data?.message || '取消取貨預約失敗');
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchAppointments();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchAppointments();
    };

    const goToPage = (page) => {
      currentPage.value = page;
      fetchAppointments();
    };

    const getStatusText = (status) => {
      const statusMap = {
        'pending': '待取貨',
        'ready': '已備貨',
        'picked_up': '已取貨',
        'expired': '已過期'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        'pending': 'badge badge-warning',
        'ready': 'badge badge-info',
        'picked_up': 'badge badge-success',
        'expired': 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('zh-TW');
    };

    const formatDateTime = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW');
    };

    onMounted(() => {
      fetchStores();
      fetchAppointments();
    });

    return {
      pageTitle,
      appointments,
      stores,
      loading,
      error,
      searchQuery,
      filterStoreId,
      filterStatus,
      filterDate,
      currentPage,
      totalPages,
      total,
      handleCreate,
      handleView,
      handleConfirmPickup,
      handleCancel,
      handleSearch,
      handleFilterChange,
      goToPage,
      fetchAppointments,
      getStatusText,
      getStatusBadgeClass,
      formatDate,
      formatDateTime
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1000px;
}
</style>
